import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { uploadDocument } from '../api/analysis';
import { Dropzone, validateFile } from '../components/Dropzone';

export function UploadPage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  function onFileSelected(file: File | null) {
    setSubmitError(null);
    setSelectedFile(file);
    if (!file) {
      setValidationError(null);
      return;
    }
    setValidationError(validateFile(file));
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedFile) {
      setValidationError('Choose a PDF, DOCX, or TXT document first.');
      return;
    }
    const nextError = validateFile(selectedFile);
    setValidationError(nextError);
    if (nextError) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const accepted = await uploadDocument(selectedFile);
      navigate(`/analysis/${accepted.analysis_id}`);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Upload failed.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="grid gap-8 lg:grid-cols-[minmax(0,1.15fr),minmax(22rem,0.85fr)] lg:items-start">
      <form className="space-y-5" onSubmit={onSubmit}>
        <Dropzone
          selectedFile={selectedFile}
          validationError={validationError}
          onFileSelected={onFileSelected}
          disabled={isSubmitting}
        />
        {submitError ? (
          <div className="rounded-[1.25rem] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {submitError}
          </div>
        ) : null}
        <button
          type="submit"
          disabled={isSubmitting || !selectedFile || Boolean(validationError)}
          className="rounded-full bg-teal-700 px-6 py-3 text-sm font-semibold text-white transition hover:bg-teal-800 disabled:cursor-not-allowed disabled:bg-slate-400"
        >
          {isSubmitting ? 'Submitting for analysis…' : 'Run AI analysis'}
        </button>
      </form>

      <aside className="rounded-[2rem] border border-slate-200 bg-slate-950 p-8 text-white shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-300">Evidence-first review</p>
        <h2 className="mt-3 text-3xl font-semibold">What the first pass includes</h2>
        <ul className="mt-5 space-y-4 text-base leading-7 text-slate-200">
          <li>Sentence-level classification with a visible confidence trail.</li>
          <li>Model-family fingerprinting to distinguish likely GPT-style output from other families.</li>
          <li>A downloadable PDF report for moderator or faculty handoff.</li>
        </ul>
      </aside>
    </section>
  );
}
